/**
 * Topic Review Service
 * Handles API calls for the topic review/feedback system
 */
import axios, { AxiosInstance } from 'axios'
import { getConfig } from '../config/Constants'
import type { ApiResponse } from '../types/api.types'

let apiInstance: AxiosInstance | null = null

function getApiInstance() {
    if (!apiInstance) {
        const config = getConfig()
        apiInstance = axios.create({
            baseURL: `${config.apiUrl}`,
            headers: {
                'Content-Type': 'application/json',
            },
        })

        // Add request interceptor to add auth token
        apiInstance.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('access_token')
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`
                }
                return config
            },
            (error) => {
                return Promise.reject(error)
            }
        )
    }
    return apiInstance
}
import type {
    TopicReviewCreateRequest,
    TopicReviewResponse,
    ReviewedTopicsResponse,
    HasReviewResponse
} from '../types/topicReview.types'

export const topicReviewService = {
    submitReview,
    getReview,
    getReviewedTopicIds,
    hasReview,
    deleteReview,
    // Beacon API for guaranteed save on page unload
    sendBeacon
}

/**
 * Submit or auto-save a topic review
 * Uses upsert - creates new or updates existing based on enrollment_id + topic_id
 */
async function submitReview(
    request: TopicReviewCreateRequest
): Promise<ApiResponse<TopicReviewResponse>> {
    try {
        const response = await getApiInstance().post('/topic-reviews/', request)
        return { success: true, data: response.data.data || response.data }
    } catch (error: any) {
        console.error('[TopicReviewService] Error submitting review:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to submit review'
        }
    }
}

/**
 * Get a review for a specific topic
 */
async function getReview(
    enrollmentId: string,
    topicId: string
): Promise<ApiResponse<TopicReviewResponse | null>> {
    try {
        const response = await getApiInstance().get(`/topic-reviews/topic/${enrollmentId}/${topicId}`)
        return { success: true, data: response.data.data || null }
    } catch (error: any) {
        console.error('[TopicReviewService] Error getting review:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to get review'
        }
    }
}

/**
 * Get list of topic IDs that have been reviewed for an enrollment
 */
async function getReviewedTopicIds(
    enrollmentId: string
): Promise<ApiResponse<ReviewedTopicsResponse>> {
    try {
        const response = await getApiInstance().get(`/topic-reviews/reviewed/${enrollmentId}`)
        return { success: true, data: response.data.data || { reviewed_topic_ids: [] } }
    } catch (error: any) {
        console.error('[TopicReviewService] Error getting reviewed topics:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to get reviewed topics'
        }
    }
}

/**
 * Quick check if a topic has been reviewed
 */
async function hasReview(
    enrollmentId: string,
    topicId: string
): Promise<ApiResponse<HasReviewResponse>> {
    try {
        const response = await getApiInstance().get(`/topic-reviews/has-review/${enrollmentId}/${topicId}`)
        return { success: true, data: response.data.data || { has_review: false } }
    } catch (error: any) {
        console.error('[TopicReviewService] Error checking review status:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to check review status'
        }
    }
}

/**
 * Delete a review
 */
async function deleteReview(
    reviewId: string
): Promise<ApiResponse<void>> {
    try {
        await getApiInstance().delete(`/topic-reviews/${reviewId}`)
        return { success: true, data: undefined }
    } catch (error: any) {
        console.error('[TopicReviewService] Error deleting review:', error)
        return {
            success: false,
            error: error.response?.data?.detail || error.message || 'Failed to delete review'
        }
    }
}

/**
 * Send review data using Beacon API for guaranteed delivery on page unload
 * This is used in beforeunload and visibilitychange handlers
 */
function sendBeacon(request: TopicReviewCreateRequest): boolean {
    try {
        const token = localStorage.getItem('access_token')
        if (!token) {
            console.warn('[TopicReviewService] No token for beacon')
            return false
        }

        // Get the API base URL
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:8002/api/v1'
        const url = `${apiUrl}/topic-reviews/`

        // Create blob with JSON data
        const blob = new Blob([JSON.stringify(request)], { type: 'application/json' })

        // Note: Beacon API doesn't support custom headers, so we need to use a different approach
        // We'll append the token as a query parameter (less secure but necessary for beacon)
        const beaconUrl = `${url}?_token=${encodeURIComponent(token)}`

        return navigator.sendBeacon(beaconUrl, blob)
    } catch (error) {
        console.error('[TopicReviewService] Beacon error:', error)
        return false
    }
}

export default topicReviewService
